import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import type { RecoveryCodes, TwoFactorSetup, TwoFactorStatus } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { config } from '../config'
import { Avatar, Card } from '../components/ui'
import { ProfilePhotoCard } from '../components/ProfilePhotoCard'
import { BirthdayCard } from '../components/Celebrations'
import { useModuleEnabled } from '../navigation'
import { PhoneLink } from '../components/SiteContact'
import { QrCode } from '../components/QrCode'
import { usePendingReceiptCount } from '../components/PendingReceiptsBanner'
import { useInvoiceReadyCountForOffice } from '../components/InvoiceReadyBanner'
import { RecoveryCodeList } from '../components/RecoveryCodeList'
// Samme lister som sidemenuen paa desktop - se src/navigation.ts
import { useVisibleAdminNav, useVisibleMoreNav } from '../navigation'

/**
 * "Mere"-siden fra bundmenuen (kravspec afsnit 48).
 * Adminpunkterne vises kun for brugere med de nødvendige rettigheder.
 */
export function MorePage() {
  // Fødselsdagen hører til funktionen "Fødselsdage og jubilæer" (beslutning 119).
  const intranetEnabled = useModuleEnabled()('intranet_celebrations')
  const pendingReceipts = usePendingReceiptCount()
  const invoiceReady = useInvoiceReadyCountForOffice()
  const { user, logout } = useAuth()

  // Rollerne og de slukkede moduler er allerede taget med i betragtning.
  // Tom liste betyder, at brugeren ikke har noget at administrere.
  const adminNav = useVisibleAdminNav()

  // Sidemenuen er skjult paa mobil. Her staar resten af hovedmenuen - det,
  // der ikke allerede er i bundmenuen.
  const moreNav = useVisibleMoreNav()

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card title="Min profil">
        <div className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
          <Avatar user={user} size={48} />
          <div className="small">
            <div style={{ fontWeight: 600 }}>{user?.displayName}</div>
            <div className="muted">{user?.email}</div>
            {user?.phoneNumber && (
              <div className="muted">
                <PhoneLink phone={user.phoneNumber} />
              </div>
            )}
            <div className="muted" style={{ marginTop: 6 }}>
              Roller: {user?.roles.join(', ') || 'Ingen'}
            </div>
            <div className="muted">
              Login: {user?.authProvider === 'local' ? 'Lokal bruger (adgangskode)' : 'Microsoft 365'}
            </div>
          </div>
        </div>
      </Card>

      {user && <ProfilePhotoCard user={user} />}

      {/* Egen foedselsdag (beslutning 117) - kun naar intranettet er slaaet til. */}
      {intranetEnabled && <BirthdayCard />}

      {/* Erstatter "Genveje": Kalender står i bundmenuen, og Notifikationer og
          Vejledningen (beslutning 089) er med her, fordi de står i hovedmenuen. */}
      {moreNav.length > 0 && (
        <Card title="Alle sider">
          <div className="stack" style={{ gap: 6 }}>
            {moreNav.map((item) => (
              <Link className="btn" key={item.to} to={item.to}>
                <span aria-hidden style={{ marginRight: 8 }}>{item.icon}</span>
                {item.label}
                {/* Kvitteringer til godkendelse - kun kontoret (Simon 2026-10-01). */}
                {item.to === '/receipts' && pendingReceipts > 0 && (
                  <span className="pill" style={{ background: '#DC2626', color: '#fff' }}>
                    {pendingReceipts} venter
                  </span>
                )}
                {item.to === '/invoicing' && invoiceReady > 0 && (
                  <span className="pill" style={{ background: '#DC2626', color: '#fff' }}>
                    {invoiceReady} klar
                  </span>
                )}
              </Link>
            ))}
          </div>
        </Card>
      )}

      {user?.authProvider === 'local' && <ChangePasswordCard mustChange={user.mustChangePassword} />}

      {user?.authProvider === 'local' && <TwoFactorCard />}

      {adminNav.length > 0 && (
        <Card title="Administration">
          <div className="stack" style={{ gap: 6 }}>
            {adminNav.map((item) => (
              <Link className="btn" key={item.to} to={item.to}>
                <span aria-hidden style={{ marginRight: 8 }}>{item.icon}</span>
                {item.label}
              </Link>
            ))}
          </div>
        </Card>
      )}

      <Card title="Om appen">
        <div className="small muted">
          {config.appName}
          <br />
          Installeret som app? Så modtager du automatisk opdateringer, næste gang du åbner den.
        </div>
        <button className="btn btn--danger" style={{ marginTop: 14 }} onClick={() => void logout()}>
          Log ud
        </button>
      </Card>
    </div>
  )
}

/**
 * To-faktor for lokale brugere (beslutning 035).
 *
 * Frivilligt for de fleste; for superadministratorer er det et krav, der
 * håndhæves ved login, og kortet kan da kun bruges til nye genoprettelseskoder.
 * Microsoft-brugere ser slet ikke kortet - de får to-faktor fra Entra.
 */
function TwoFactorCard() {
  const queryClient = useQueryClient()
  const { refreshProfile } = useAuth()
  const { data: status } = useQuery({
    queryKey: ['two-factor'],
    queryFn: () => api.get<TwoFactorStatus>('/api/auth/two-factor')
  })

  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null)
  const [codes, setCodes] = useState<string[] | null>(null)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)
  const [busy, setBusy] = useState(false)

  async function run(action: () => Promise<void>) {
    setMessage(null)
    setBusy(true)
    try {
      await action()
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'Handlingen mislykkedes.' })
    } finally {
      setBusy(false)
    }
  }

  async function reload() {
    await queryClient.invalidateQueries({ queryKey: ['two-factor'] })
    await refreshProfile()
  }

  if (!status) {
    return null
  }

  // Koderne vises én gang og forsvinder, når kortet lukkes.
  if (codes) {
    return (
      <Card title="To-faktor">
        <RecoveryCodeList codes={codes} onDone={() => setCodes(null)} />
      </Card>
    )
  }

  return (
    <Card title="To-faktor (engangskode)">
      {status.enabled ? (
        <>
          <p className="small" style={{ marginTop: 0, color: 'var(--success)' }}>
            To-faktor er slået til
            {status.required && ' - og påkrævet for din rolle, så det kan ikke slås fra'}.
          </p>
          <p className="small muted">
            {status.recoveryCodesLeft} genoprettelseskode{status.recoveryCodesLeft === 1 ? '' : 'r'} tilbage.
            {status.recoveryCodesLeft <= 2 && ' Lav nye, mens du stadig kan komme ind.'}
          </p>

          <label className="field">
            <span className="field__label">Bekræft med din adgangskode</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          {!status.required && (
            <label className="field">
              <span className="field__label">Kode fra appen (kun for at slå fra)</span>
              <input autoComplete="one-time-code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} />
            </label>
          )}

          <div className="row" style={{ gap: 8 }}>
            <button
              className="btn btn--sm"
              disabled={busy || !password}
              onClick={() =>
                void run(async () => {
                  const result = await api.post<RecoveryCodes>('/api/auth/two-factor/recovery-codes', { password })
                  setPassword('')
                  setCodes(result.recoveryCodes)
                  await reload()
                })
              }
            >
              Nye genoprettelseskoder
            </button>

            {!status.required && (
              <button
                className="btn btn--danger btn--sm"
                disabled={busy || !password || !code}
                onClick={() =>
                  void run(async () => {
                    await api.post('/api/auth/two-factor/disable', { password, code: code.trim() })
                    setPassword('')
                    setCode('')
                    setMessage({ ok: true, text: 'To-faktor er slået fra.' })
                    await reload()
                  })
                }
              >
                Slå fra
              </button>
            )}
          </div>
        </>
      ) : setup ? (
        <>
          <ol className="small muted" style={{ paddingLeft: 18, marginTop: 0 }}>
            <li>Åbn Microsoft Authenticator.</li>
            <li>
              Vælg <strong>Tilføj konto</strong> → <strong>Anden konto</strong>.
            </li>
            <li>Scan koden, og tast de seks cifre herunder.</li>
          </ol>

          <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0 10px' }}>
            <QrCode value={setup.uri} size={180} />
          </div>
          <p className="tiny muted" style={{ textAlign: 'center' }}>
            Kan du ikke scanne? Nøgle: <code style={{ wordBreak: 'break-all' }}>{setup.secret}</code>
          </p>

          <label className="field">
            <span className="field__label">Kode fra appen</span>
            <input
              autoComplete="one-time-code"
              inputMode="numeric"
              placeholder="123 456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </label>

          <div className="row" style={{ gap: 8 }}>
            <button
              className="btn btn--primary btn--sm"
              disabled={busy || !code}
              onClick={() =>
                void run(async () => {
                  const result = await api.post<RecoveryCodes>('/api/auth/two-factor/confirm', { code: code.trim() })
                  setCode('')
                  setSetup(null)
                  setCodes(result.recoveryCodes)
                  await reload()
                })
              }
            >
              Slå to-faktor til
            </button>
            <button className="btn btn--sm" disabled={busy} onClick={() => setSetup(null)}>
              Annullér
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="small muted" style={{ marginTop: 0 }}>
            Med to-faktor skal der både bruges din adgangskode og en engangskode fra Microsoft Authenticator for at
            logge ind.
          </p>

          <label className="field">
            <span className="field__label">Bekræft med din adgangskode</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          <button
            className="btn btn--primary btn--sm"
            disabled={busy || !password}
            onClick={() =>
              void run(async () => {
                setSetup(await api.post<TwoFactorSetup>('/api/auth/two-factor/setup', { password }))
                setPassword('')
              })
            }
          >
            Sæt to-faktor op
          </button>
        </>
      )}

      {message && (
        <div
          className={message.ok ? 'small' : 'error-box'}
          style={{ marginTop: 12, color: message.ok ? 'var(--success)' : undefined }}
        >
          {message.text}
        </div>
      )}
    </Card>
  )
}

function ChangePasswordCard({ mustChange }: { mustChange: boolean }) {
  const { refreshProfile } = useAuth()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setMessage(null)
    if (next !== repeat) {
      setMessage({ ok: false, text: 'De to nye adgangskoder er ikke ens.' })
      return
    }
    setBusy(true)
    try {
      await api.post('/api/auth/change-password', { currentPassword: current, newPassword: next })
      setCurrent('')
      setNext('')
      setRepeat('')
      setMessage({ ok: true, text: 'Adgangskoden er skiftet.' })
      await refreshProfile()
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'Kunne ikke skifte adgangskode.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Skift adgangskode">
      {mustChange && (
        <div className="error-box" style={{ marginBottom: 12, background: '#FFF7ED', borderColor: '#FED7AA', color: '#9A3412' }}>
          Du har fået en midlertidig adgangskode. Skift den nu.
        </div>
      )}
      <form onSubmit={submit}>
        <label className="field">
          <span className="field__label">Nuværende adgangskode</span>
          <input type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
        </label>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Ny adgangskode (min. 8 tegn, bogstaver og tal)</span>
            <input type="password" autoComplete="new-password" required value={next} onChange={(e) => setNext(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Gentag ny adgangskode</span>
            <input type="password" autoComplete="new-password" required value={repeat} onChange={(e) => setRepeat(e.target.value)} />
          </label>
        </div>
        {message && (
          <div className={message.ok ? 'small' : 'error-box'} style={{ marginBottom: 10, color: message.ok ? 'var(--success)' : undefined }}>
            {message.text}
          </div>
        )}
        <button className="btn btn--primary btn--sm" type="submit" disabled={busy}>
          Gem ny adgangskode
        </button>
      </form>
    </Card>
  )
}
